import { useState } from "react";
import { Ruler, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useWeight } from "@/contexts/WeightContext";

const BROSELOW_ZONES = [
  { color: "Grey",   lengthMin: 46,  lengthMax: 56,  weight: 3,  age: "Newborn",     ettSize: "3.0", defib: 6,   kingLT: "0",    blade: "Miller 0",   bg: "bg-gray-500",   text: "text-white" },
  { color: "Grey+",  lengthMin: 57,  lengthMax: 60,  weight: 4,  age: "1-3 mo",      ettSize: "3.0", defib: 8,   kingLT: "0",    blade: "Miller 0",   bg: "bg-gray-400",   text: "text-white" },
  { color: "Pink",   lengthMin: 61,  lengthMax: 67,  weight: 5,  age: "3-5 mo",      ettSize: "3.5", defib: 10,  kingLT: "1",    blade: "Miller 1",   bg: "bg-pink-400",   text: "text-white" },
  { color: "Pink+",  lengthMin: 68,  lengthMax: 73,  weight: 6,  age: "5-8 mo",      ettSize: "3.5", defib: 12,  kingLT: "1",    blade: "Miller 1",   bg: "bg-pink-500",   text: "text-white" },
  { color: "Red",    lengthMin: 74,  lengthMax: 80,  weight: 8,  age: "8-11 mo",     ettSize: "4.0", defib: 16,  kingLT: "1",    blade: "Miller 1",   bg: "bg-red-500",    text: "text-white" },
  { color: "Red+",   lengthMin: 81,  lengthMax: 85,  weight: 9,  age: "10-12 mo",    ettSize: "4.0", defib: 18,  kingLT: "1",    blade: "Miller 1",   bg: "bg-red-600",    text: "text-white" },
  { color: "Purple", lengthMin: 86,  lengthMax: 95,  weight: 10, age: "1-2 yr",      ettSize: "4.5", defib: 20,  kingLT: "1",    blade: "Miller 1-2", bg: "bg-purple-500", text: "text-white" },
  { color: "Yellow", lengthMin: 96,  lengthMax: 107, weight: 12, age: "2-3 yr",      ettSize: "4.5", defib: 24,  kingLT: "1",    blade: "Miller 2",   bg: "bg-yellow-400", text: "text-black" },
  { color: "White",  lengthMin: 108, lengthMax: 116, weight: 15, age: "4-5 yr",      ettSize: "5.0", defib: 30,  kingLT: "2",    blade: "Mac 2",      bg: "bg-white",      text: "text-black", border: "border-gray-300" },
  { color: "Blue",   lengthMin: 117, lengthMax: 125, weight: 19, age: "6-7 yr",      ettSize: "5.0", defib: 38,  kingLT: "2",    blade: "Mac 2",      bg: "bg-blue-500",   text: "text-white" },
  { color: "Orange", lengthMin: 126, lengthMax: 137, weight: 24, age: "8-9 yr",      ettSize: "5.5", defib: 48,  kingLT: "2",    blade: "Mac 2-3",    bg: "bg-orange-500", text: "text-white" },
  { color: "Green",  lengthMin: 138, lengthMax: 150, weight: 30, age: "10-11 yr",    ettSize: "5.5", defib: 60,  kingLT: "2.5",  blade: "Mac 3",      bg: "bg-green-500",  text: "text-white" },
];

// Age-based weight estimation (when length isn't available)
const AGE_TO_WEIGHT = [
  { label: "Newborn",  months: 0,   weight: 3 },
  { label: "3 mo",     months: 3,   weight: 5 },
  { label: "6 mo",     months: 6,   weight: 7 },
  { label: "9 mo",     months: 9,   weight: 8 },
  { label: "1 yr",     months: 12,  weight: 10 },
  { label: "2 yr",     months: 24,  weight: 12 },
  { label: "3 yr",     months: 36,  weight: 14 },
  { label: "4 yr",     months: 48,  weight: 16 },
  { label: "5 yr",     months: 60,  weight: 18 },
  { label: "6 yr",     months: 72,  weight: 20 },
  { label: "7 yr",     months: 84,  weight: 22 },
  { label: "8 yr",     months: 96,  weight: 25 },
  { label: "9 yr",     months: 108, weight: 28 },
  { label: "10 yr",    months: 120, weight: 32 },
  { label: "11 yr",    months: 132, weight: 35 },
  { label: "12 yr",    months: 144, weight: 40 },
];

export default function BroselowEstimator() {
  const [inputMode, setInputMode] = useState("length"); // "length" or "age"
  const [lengthCm, setLengthCm] = useState("");
  const [selectedAge, setSelectedAge] = useState("");
  const [selectedZone, setSelectedZone] = useState(null);
  const { weight: globalWeight, setWeight: setGlobalWeight, hasWeight } = useWeight();

  const handleLengthChange = (val) => {
    const cleaned = val.replace(/[^0-9]/g, "");
    setLengthCm(cleaned);
    setSelectedAge("");
    if (!cleaned) { setSelectedZone(null); return; }
    const cm = parseInt(cleaned);
    const zone = BROSELOW_ZONES.find(z => cm >= z.lengthMin && cm <= z.lengthMax);
    setSelectedZone(zone || null);
    if (zone) setGlobalWeight(String(zone.weight));
  };

  const handleAgeChange = (val) => {
    setSelectedAge(val);
    setLengthCm("");
    if (!val) { setSelectedZone(null); return; }
    const months = parseInt(val);
    const ageEntry = AGE_TO_WEIGHT.find(a => a.months === months);
    if (ageEntry) {
      setGlobalWeight(String(ageEntry.weight));
      // Find closest Broselow zone by weight
      const zone = BROSELOW_ZONES.reduce((closest, z) =>
        Math.abs(z.weight - ageEntry.weight) < Math.abs(closest.weight - ageEntry.weight) ? z : closest
      , BROSELOW_ZONES[0]);
      setSelectedZone(zone);
    }
  };

  const selectZone = (zone) => {
    setSelectedZone(zone);
    setLengthCm(String(zone.lengthMin));
    setSelectedAge("");
    setGlobalWeight(String(zone.weight));
  };

  const reset = () => { setLengthCm(""); setSelectedAge(""); setSelectedZone(null); };

  return (
    <Card className="border-purple-500/30" data-testid="broselow-estimator">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="flex items-center justify-between text-base font-heading">
          <span className="flex items-center gap-2">
            <Ruler className="w-4 h-4 text-purple-400" />
            Broselow Weight Estimator
          </span>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={reset} data-testid="broselow-reset">
            <RotateCcw className="w-3 h-3 mr-1" />Reset
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-3">
        {/* Input Mode Toggle */}
        <div className="flex gap-2 items-center">
          <button
            onClick={() => setInputMode("length")}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${inputMode === "length" ? "bg-purple-500/20 text-purple-300 border-purple-500/40 font-medium" : "border-border hover:border-purple-500/30"}`}
          >
            By Length
          </button>
          <button
            onClick={() => setInputMode("age")}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${inputMode === "age" ? "bg-purple-500/20 text-purple-300 border-purple-500/40 font-medium" : "border-border hover:border-purple-500/30"}`}
          >
            By Age
          </button>
        </div>

        {/* Length or Age Input */}
        <div className="flex items-center gap-3">
          <div className="flex-1">
            {inputMode === "length" ? (
              <>
                <label className="text-xs text-muted-foreground mb-1 block">Patient length (cm)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 75"
                  value={lengthCm}
                  onChange={(e) => handleLengthChange(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border bg-background text-lg font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="broselow-length-input"
                />
              </>
            ) : (
              <>
                <label className="text-xs text-muted-foreground mb-1 block">Patient age</label>
                <select
                  value={selectedAge}
                  onChange={(e) => handleAgeChange(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border bg-background text-lg focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="broselow-age-input"
                >
                  <option value="">Select age...</option>
                  {AGE_TO_WEIGHT.map((a) => (
                    <option key={a.months} value={a.months}>{a.label} (~{a.weight} kg)</option>
                  ))}
                </select>
              </>
            )}
          </div>
          {selectedZone && (
            <div className={`text-center px-4 py-2 rounded-lg ${selectedZone.bg} ${selectedZone.text} ${selectedZone.border || ""} border min-w-[120px]`} data-testid="broselow-result">
              <div className="text-2xl font-bold font-mono">{selectedZone.weight} kg</div>
              <div className="text-xs font-medium opacity-80">{selectedZone.color} Zone</div>
              <div className="text-[10px] opacity-60">{selectedZone.age}</div>
            </div>
          )}
        </div>

        {/* Quick Reference Row */}
        {selectedZone && (
          <div className="grid grid-cols-5 gap-1.5" data-testid="broselow-quick-ref">
            <div className="bg-muted/50 rounded p-2 text-center">
              <div className="text-lg font-bold font-mono">{selectedZone.ettSize}</div>
              <div className="text-[10px] text-muted-foreground">ETT Size</div>
            </div>
            <div className="bg-muted/50 rounded p-2 text-center">
              <div className="text-lg font-bold font-mono">{selectedZone.blade}</div>
              <div className="text-[10px] text-muted-foreground">Blade</div>
            </div>
            <div className="bg-muted/50 rounded p-2 text-center">
              <div className="text-lg font-bold font-mono">{selectedZone.kingLT}</div>
              <div className="text-[10px] text-muted-foreground">King LT</div>
            </div>
            <div className="bg-muted/50 rounded p-2 text-center">
              <div className="text-lg font-bold font-mono">{selectedZone.defib}J</div>
              <div className="text-[10px] text-muted-foreground">Defib (2J/kg)</div>
            </div>
            <div className={`rounded p-2 text-center border ${hasWeight ? 'bg-green-500/10 border-green-500/30' : 'bg-muted/50'}`}>
              <div className="text-lg font-bold font-mono">{globalWeight || "—"} kg</div>
              <div className="text-[10px] text-muted-foreground">Weight Set</div>
            </div>
          </div>
        )}

        {/* Color Zone Tape */}
        <div>
          <div className="text-xs text-muted-foreground mb-1.5">Tap a zone or enter length above</div>
          <div className="flex flex-wrap gap-1" data-testid="broselow-zones">
            {BROSELOW_ZONES.map((zone) => (
              <button
                key={zone.color}
                onClick={() => selectZone(zone)}
                className={`px-2 py-1.5 rounded text-xs font-medium transition-all ${zone.bg} ${zone.text} ${zone.border || ""} border ${
                  selectedZone?.color === zone.color ? "ring-2 ring-primary ring-offset-2 ring-offset-background scale-105" : "opacity-80 hover:opacity-100"
                }`}
                data-testid={`broselow-zone-${zone.color.toLowerCase()}`}
              >
                <div className="font-bold">{zone.weight}kg</div>
                <div className="text-[9px] opacity-70">{zone.age}</div>
              </button>
            ))}
          </div>
        </div>

        {/* No match warning */}
        {lengthCm && parseInt(lengthCm) > 0 && !selectedZone && (
          <p className="text-xs text-amber-400 text-center py-1">
            {parseInt(lengthCm) < 46 ? "Below Broselow range — use clinical judgment for neonates" : "Above Broselow range — use adult weight estimation"}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
